import React from 'react';
import { Box, Paper, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, FormGroup, Checkbox, Typography, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Question } from './FeedbackConfig';

interface QuestionRendererProps {
  question: Question;
  questionResponses: Record<string, any>;
  handleQuestionResponse: (questionId: string, value: any) => void;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({ question, questionResponses, handleQuestionResponse }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const labelColor = theme.palette.text.primary;
  const textColor = theme.palette.text.primary;

  switch (question.type) {
    case 'radio':
      return (
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50]
            }}
          >
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel
                component="legend"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: labelColor,
                  '&.Mui-focused': {
                    color: labelColor
                  }
                }}
              >
                {question.question} {question.required && <span style={{ color: theme.palette.error.main }}>*</span>}
              </FormLabel>
              <RadioGroup value={questionResponses[question.id] || ''} onChange={e => handleQuestionResponse(question.id, e.target.value)}>
                {question.options?.map(option => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio color="primary" />}
                    label={<Typography sx={{ color: textColor, fontSize: '0.95rem' }}>{option}</Typography>}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Paper>
        </Box>
      );

    case 'checkbox':
      return (
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50]
            }}
          >
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel
                component="legend"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: labelColor,
                  '&.Mui-focused': {
                    color: labelColor
                  }
                }}
              >
                {question.question}
              </FormLabel>
              <FormGroup>
                {question.options?.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={questionResponses[question.id]?.includes(option) || false}
                        onChange={e => {
                          const current = questionResponses[question.id] || [];
                          const newValue = e.target.checked ? [...current, option] : current.filter((item: string) => item !== option);
                          handleQuestionResponse(question.id, newValue);
                        }}
                        color="primary"
                      />
                    }
                    label={<Typography sx={{ color: textColor, fontSize: '0.95rem' }}>{option}</Typography>}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Paper>
        </Box>
      );

    case 'text':
      return (
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50]
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: 600, color: textColor }}
            >
              {question.question}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={questionResponses[question.id] || ''}
              onChange={e => handleQuestionResponse(question.id, e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  '& input, & textarea': { color: textColor },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Paper>
        </Box>
      );

    default:
      return null;
  }
};

export default QuestionRenderer;